import { Box, Card, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import {
    FONT_WEIGHT,
    PageHeading,
    PageSubHeading
} from '@/typography/Typography';
import { useEffect } from 'react';
import {
    useCurrentCompanyDetails,
    useTipsHistoryDetails
} from '@/services/paymentsQuery';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import {
    TablePagination,
    usePagination
} from '@/components/shared/TablePagination';
import { Link, useSearchParams } from 'react-router-dom';
import { TipsEmployeeList } from './TipsEmployeeList';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { DATE_FORMAT } from '@/lib/constants';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { SecondaryButtonRegular } from '@/components/shared/Button';
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';
import { useFormatting } from '@/utils/formatting';
import { TipsStatus } from '@/services/PaymentsServiceV2';
import dayjs from 'dayjs';

export const TipsEmployeeListPage = () => {
    // get posLocationId and date from query params
    const [searchParams] = useSearchParams();
    const posLocationId = searchParams.get('posLocationId');
    const date = searchParams.get('date');
    const status = searchParams.get('status');
    const { page, rowsPerPage, setRowsPerPage } = usePagination(-1);
    const { toDecimal } = useFormatting();

    const { data: companyData } = useCurrentCompanyDetails();
    const isValidationMode =
        companyData?.data?.hasInstaTips === true &&
        companyData?.data?.tipsLive === false;

    const { data, error, isPending } = useTipsHistoryDetails(
        posLocationId!, // posLocationId
        date!, // date
        true // enabled
    );

    useHandleResponse({
        error: error
    });

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setLoader(isPending));
    }, [isPending]);

    // Format the date string from the API response
    const dataStr = data?.data?.date
        ? dayjs(data.data.date).format(DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR)
        : '';

    const handleDownloadExcel = () => {
        const sheet = XLSXUtils.aoa_to_sheet([]);

        // Define headers for the Excel file
        const headers = [
            t('Name'),
            t('Shift'),
            t('Reported'),
            t('Employment group'),
            t('Status'),
            t('Worked hours'),
            t('Share of tips'),
            t('Earned tips')
        ];

        // Add headers with bold style
        XLSXUtils.sheet_add_aoa(sheet, [headers], { origin: 'A1' });
        headers.forEach((header, idx) => {
            const cellAddress = XLSXUtils.encode_cell({ r: 0, c: idx });
            if (!sheet[cellAddress]) sheet[cellAddress] = { t: 's', v: header };
            sheet[cellAddress].s = {
                font: { bold: true }
            };
        });

        // Add data rows - get tipsHistoryDetail from singleJournalView
        const items = data?.data?.singleJournalView?.tipsHistoryDetail || [];
        if (items.length > 0) {
            const rows = items.map((detail) => [
                detail.name,
                detail.shiftStatus,
                detail.reportedAt ? 'Yes' : '',
                detail.employeeGroupName,
                detail.isFirstLogin
                    ? 'Invited'
                    : detail.isDisabled
                    ? 'Deactivated'
                    : detail.isActive
                    ? 'Active'
                    : 'Inactive',
                toDecimal(detail.workedHours, 1),
                toDecimal(detail.shareOfTips, 2),
                detail.tipsDistributed
                    ? detail.earnedTips
                    : detail.estimatedTips
            ]);

            XLSXUtils.sheet_add_aoa(sheet, rows, { origin: 'A2' });
        }

        // Add footer with totals
        if (items.length > 0) {
            const footerRow = [
                t('total_date', { date: dataStr }),
                '',
                '',
                '',
                '',
                toDecimal(data?.data?.totalHours, 1),
                '',
                data?.data?.totalTipsAmount
            ];
            XLSXUtils.sheet_add_aoa(sheet, [footerRow], {
                origin: `A${items.length + 2}`
            });
        }

        const workbook = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(workbook, sheet, 'Tips Employee List');

        const fileName = `Tips_Employee_List_${
            dataStr || new Date().toISOString().split('T')[0]
        }.xlsx`;
        XLSXWriteFile(workbook, fileName, {
            bookType: 'xlsx',
            type: 'binary'
        });
    };

    return (
        <div>
            <Box mt={1} ml={1} display={'flex'} alignItems={'center'}>
                <Link to={-1 as any}>
                    <ArrowBackIosIcon />
                </Link>
                <PageHeading ml={2}>
                    {t('tips.tips') + ' - ' + (data?.data?.locationName ?? '')}
                    <Chip
                        color={
                            status == TipsStatus.Awaiting
                                ? 'warning'
                                : undefined
                        }
                        sx={{ marginLeft: 2 }}
                        label={dataStr}
                    ></Chip>
                </PageHeading>
            </Box>

            <PageSubHeading>{t('tips.review_tips')}</PageSubHeading>

            <Card sx={{ marginTop: 2 }}>
                <CardHeader
                    label={t('tips.employees')}
                    chip={
                        <Chip
                            icon={<span></span>}
                            label={
                                <Typography fontWeight={FONT_WEIGHT.SEMI_BOLD}>
                                    {t('filtered_of_total', {
                                        filtered:
                                            data?.data?.totalTipRecords ?? 0,
                                        total: data?.data?.totalTipRecords ?? 0
                                    })}
                                </Typography>
                            }
                            color={'success'}
                        />
                    }
                    actions={
                        <Stack direction="row" gap={'8px'}>
                            <SecondaryButtonRegular
                                onClick={handleDownloadExcel}
                                size="small"
                                sx={{ borderRadius: '5px' }}
                            >
                                {t('download_as_excel')}
                            </SecondaryButtonRegular>
                        </Stack>
                    }
                />
                <TipsEmployeeList
                    items={data?.data?.singleJournalView?.tipsHistoryDetail}
                    totalEarnedTips={data?.data?.totalTipsAmount ?? 0}
                    totalWorkedHours={data?.data?.totalHours ?? 0}
                    reloadTransactions={() => {}}
                    date={dataStr}
                ></TipsEmployeeList>
                {(data?.data?.singleJournalView?.tipsHistoryDetail?.length ??
                    0) == 0 && (
                    <Typography m={1} textAlign={'center'}>
                        {t('no_records')}
                    </Typography>
                )}
                <TablePagination
                    page={page}
                    rowsPerPageOptions={[
                        // {
                        //     label: '100',
                        //     value: 100
                        // },
                        // {
                        //     label: '100',
                        //     value: 200
                        // },
                        // {
                        //     label: '300',
                        //     value: 300
                        // },
                        {
                            label: 'All',
                            value: -1
                        }
                    ]}
                    count={data?.data?.totalTipRecords ?? 0}
                    rowsPerPage={rowsPerPage}
                    onPageChange={() => {}}
                    onRowsPerPageChange={(newRowsPerPage) => {
                        setRowsPerPage(newRowsPerPage as number);
                    }}
                />
            </Card>
        </div>
    );
};
